import { inject } from '@angular/core';
import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { map } from 'rxjs';
import { RoleGuard } from './core/auth/guards/role.guard';
import { Role } from './core/user/user.types';

/**
 * Écran d'accueil : l'admin arrive sur la liste des projets, le client sur son
 * tableau de bord. `check()` est attendu avant de trancher, sinon le rôle est
 * encore inconnu quand l'application est ouverte directement sur '/' (rechargement,
 * retour de SSO) et tout le monde partirait sur le tableau de bord.
 */
const homeRedirect = () => {
    const authService = inject(AuthService);

    return authService
        .check()
        .pipe(
            map(() =>
                authService.user()?.role === Role.Admin
                    ? '/projects'
                    : '/dashboard',
            ),
        );
};

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
    { path: '', pathMatch: 'full', redirectTo: homeRedirect },

    // Après la connexion, la page de sign-in renvoie sur 'signed-in-redirect',
    // qui repasse par le chemin vide pour appliquer la même règle d'accueil.
    { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: '' },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'sign-in',
                // path: 'alt-sign-in',
                loadChildren: () =>
                    import('app/modules/auth/sign-in/sign-in.routes'),
            },
            {
                path: 'forgot-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/forgot-password/forgot-password.routes'
                    ),
            },
            {
                path: 'reset-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/reset-password/reset-password.routes'
                    ),
            },
            {
                path: 'authredirect',
                loadChildren: (): Promise<any> =>
                    import(
                        'app/modules/auth/authredirect/authredirect.routing'
                    ).then((m) => m.authRedirectRoutes),
            },
        ],
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'sign-out',
                loadChildren: () =>
                    import('app/modules/auth/sign-out/sign-out.routes'),
            },
        ],
    },
    // Customer routes
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        canMatch: [RoleGuard([Role.Customer])],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        data: {
            layout: 'customer',
        },
        children: [
            {
                path: '',
                loadChildren: () =>
                    import('app/modules/customer/customer.routes')
            },
        ],
    },
    // Admin routes
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        canMatch: [RoleGuard([Role.Admin])],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: '',
                loadChildren: () =>
                    import('app/modules/admin/admin.routes')
            },

        ],
    },
    { path: '**', redirectTo: '' },
];
